import { useCallback, useEffect, useState } from "react";
import {
  AvailabilityStatus,
  type BookingSubmission,
} from "@photography-booking/shared";
import { Section } from "../components/Section.js";
import { AvailabilityCalendar } from "../components/AvailabilityCalendar.js";
import { BookingForm } from "../components/BookingForm.js";
import {
  addMonths,
  currentYearMonth,
  monthRange,
  type YearMonth,
} from "../lib/calendar.js";
import {
  fetchAvailabilityRange,
  submitBooking,
  type SubmitBookingResult,
} from "../api/client.js";

interface BookingProps {
  /**
   * Availability fetcher; defaults to the live API client. Injectable so the
   * section can be exercised without a network.
   */
  fetchRange?: (from: string, to: string) => Promise<
    { date: string; status: AvailabilityStatus }[]
  >;
  /** Booking submitter; defaults to the live API client. */
  submit?: (submission: BookingSubmission) => Promise<SubmitBookingResult>;
}

/**
 * Booking section (Req 6.1, 6.7, 8.2, 8.3): the status-only availability picker
 * paired with the "Let's get started!" inquiry form.
 *
 * The calendar requests the AVAILABLE/UNAVAILABLE status of every day in the
 * visible month and lets the Visitor select an available date; that date feeds
 * the form, whose client-side validation mirrors the shared schema. The two
 * data dependencies (availability fetch, booking submit) are injected with live
 * API clients by default. Responsive two-column-to-stacked layout keeps it
 * usable across macOS/iOS, Windows, and Android browsers (Req 6.7).
 */
export function Booking({
  fetchRange = fetchAvailabilityRange,
  submit = submitBooking,
}: BookingProps): JSX.Element {
  const [view, setView] = useState<YearMonth>(() => currentYearMonth());
  const [statuses, setStatuses] = useState<ReadonlyMap<string, AvailabilityStatus>>(
    () => new Map(),
  );
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const { from, to } = monthRange(view);

    setLoading(true);
    setLoadError(null);

    fetchRange(from, to)
      .then((results) => {
        if (cancelled) {
          return;
        }
        const next = new Map<string, AvailabilityStatus>();
        for (const result of results) {
          next.set(result.date, result.status);
        }
        setStatuses(next);
      })
      .catch(() => {
        if (!cancelled) {
          setStatuses(new Map());
          setLoadError(
            "We couldn't load availability right now. Please try again shortly.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [view, fetchRange]);

  const handleSelectDate = useCallback(
    (iso: string) => {
      // Guard against selecting a day that isn't actually available.
      if (statuses.get(iso) === AvailabilityStatus.AVAILABLE) {
        setSelectedDate(iso);
      }
    },
    [statuses],
  );

  return (
    <Section
      id="booking"
      title="Let's get started!"
      subtitle="Choose an available date and tell us about your session"
    >
      <div className="booking">
        <div className="booking__calendar">
          <AvailabilityCalendar
            view={view}
            statuses={statuses}
            loading={loading}
            error={loadError}
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
            onPrevMonth={() => setView((current) => addMonths(current, -1))}
            onNextMonth={() => setView((current) => addMonths(current, 1))}
          />
        </div>
        <div className="booking__form">
          <BookingForm selectedDate={selectedDate} onSubmit={submit} />
        </div>
      </div>
    </Section>
  );
}
